"use strict";
// Copyright (C) Microsoft Corporation. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.isNavigable = exports.getParentGamepadInteractable = exports.getParentGroupOrBody = exports.getParentScrollables = exports.getParentGroups = exports.getParentFocusContainer = void 0;
const trappable_1 = require("../../components/trappable");
const group_1 = require("../../components/group");
const scrollable_1 = require("../../components/scrollable");
const identity_1 = require("./identity");
/**
 * Find the parent focus container to a given child
 *
 *  @param view - the view whose parent focus containers we want
 *
 *  @returns the parent focus container
 */
const getParentFocusContainer = (view) => {
    if (!view) {
        return undefined;
    }
    let viewParent = view.parentElement;
    while (viewParent != null) {
        const focusContainer = (0, trappable_1.getFocusContainer)(viewParent);
        if (focusContainer) {
            return focusContainer;
        }
        viewParent = viewParent.parentElement;
    }
    return undefined;
};
exports.getParentFocusContainer = getParentFocusContainer;
/**
 * Recursively find all parent groups of a given child, stopping at the head focus container
 *
 *  @param view - the view whose parent groups we want
 *
 *  @returns the parent groups, in order such that the highest up the tree are last
 */
const getParentGroups = (view) => {
    if (!view) {
        return [];
    }
    const parentFocusGroups = [];
    let viewParent = view.parentElement;
    while (viewParent != null) {
        const focusGroup = (0, group_1.getGroup)(viewParent);
        if (focusGroup) {
            parentFocusGroups.push(focusGroup);
        }
        if ((0, trappable_1.isFocusContainer)(viewParent)) {
            break;
        }
        viewParent = viewParent.parentElement;
    }
    return parentFocusGroups;
};
exports.getParentGroups = getParentGroups;
/**
 * Recursively find all parent gamepad scrollables of a given child
 *
 *  @param child - the view whose parent gamepad scrollables we want
 *
 *  @returns the parent gamepad scrollables, in order such that the highest up the tree are last
 */
const getParentScrollables = (view, predefinedParents) => {
    const parentGamepadScrollables = [];
    if (!view) {
        return parentGamepadScrollables;
    }
    // Start from the view's parent or from the first element of predefined parents array if provided
    let currentElement = predefinedParents && predefinedParents.length > 0 ? predefinedParents[0] : view.parentElement;
    const getElementParent = () => {
        var _a;
        // If predefinedParents is provided, shift to get the next element in the array
        if (predefinedParents && predefinedParents.length > 0) {
            predefinedParents.shift();
            return predefinedParents[0];
        }
        else {
            // Otherwise, just move up the DOM tree
            return (_a = currentElement === null || currentElement === void 0 ? void 0 : currentElement.parentElement) !== null && _a !== void 0 ? _a : null;
        }
    };
    while (currentElement != null) {
        if ((0, trappable_1.isFocusContainer)(currentElement)) {
            return parentGamepadScrollables;
        }
        const gamepadScrollable = (0, scrollable_1.getScrollable)(currentElement, true);
        if (gamepadScrollable) {
            parentGamepadScrollables.push(gamepadScrollable);
            if (gamepadScrollable.doesStopScrollPropagation() || gamepadScrollable === (0, scrollable_1.getBodyScrollable)()) {
                return parentGamepadScrollables;
            }
        }
        // Get the next parent element
        currentElement = getElementParent();
    }
    // Check for the body scrollable as a fallback
    const bodyScrollable = (0, scrollable_1.getBodyScrollable)();
    if (bodyScrollable) {
        parentGamepadScrollables.push(bodyScrollable);
    }
    return parentGamepadScrollables;
};
exports.getParentScrollables = getParentScrollables;
/**
 * Recursively find the parent grouping of a given child. Groupings are focus containers,
 * focus groups, scrollables, or nested interactables
 *
 *  @param view - the view whose parent grouping we want
 *
 *  @returns the parent grouping
 */
const getParentGroupOrBody = (view) => {
    let viewParent = view.parentElement;
    while (viewParent != null) {
        if ((0, group_1.isGroup)(viewParent)) {
            return viewParent;
        }
        viewParent = viewParent.parentElement;
    }
    if (view !== document.body) {
        return document.body;
    }
};
exports.getParentGroupOrBody = getParentGroupOrBody;
/**
 * Web Only, Recursively find the parent gamepad interactable of a given child
 *
 *  @param view - the view whose parent gamepad interactable we want
 *
 *  @returns the parent gamepad interactable
 */
const getParentGamepadInteractable = (view) => {
    if (!view) {
        return undefined;
    }
    let viewParent = view.parentElement;
    while (viewParent != null) {
        const view = viewParent;
        viewParent = viewParent.parentElement;
        if ((0, identity_1.isFocusable)(view)) {
            return view;
        }
    }
};
exports.getParentGamepadInteractable = getParentGamepadInteractable;
const isNavigable = (view) => {
    if (!view) {
        return false;
    }
    let current = view;
    while (current != null) {
        const view = current;
        current = current.parentElement;
        if ((0, identity_1.isNonNavigable)(view)) {
            return false;
        }
    }
    return true;
};
exports.isNavigable = isNavigable;
//# sourceMappingURL=data:application/json;base64,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